import { APICONFIG } from '@config/api.config';
import { APIService } from '@services/api/common-services';
import { useQuery } from '@tanstack/react-query';
import { IQueryResponse } from '@services/api';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '../profile';

export interface IRbdmBudgetAllocated {
	mh3empcode_c: string;
	year_c: string;
	total_estimate_balance_c: number;
	quarter_1_budget_c?: number;
	quarter_1_budget_balance_c?: number;
	quarter_1_budget_deduction_c?: number;
	quarter_2_budget_c?: number;
	quarter_2_budget_balance_c?: number;
	quarter_2_budget_deduction_c?: number;
	quarter_3_budget_c?: number;
	quarter_3_budget_balance_c?: number;
	quarter_3_budget_deduction_c?: number;
	quarter_4_budget_c?: number;
	quarter_4_budget_balance_c?: number;
	quarter_4_budget_deduction_c?: number;
}

export interface ICurrentQuarterBudget {
	quarter: string;
	budget_c: number;
	budget_balance_c: number;
	budget_deduction_c: number;
}

// Utility functions extracted to reduce cognitive complexity
const getCurrentYear = (): string => {
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const nextYear = currentYear + 1;
	return `${currentYear}-${nextYear.toString().slice(-2)}`;
};

const getCurrentQuarter = (): string => {
	const month = new Date().getMonth() + 1;
	const quarterRanges = [
		{ min: 1, max: 3, quarter: 'Q1' },
		{ min: 4, max: 6, quarter: 'Q2' },
		{ min: 7, max: 9, quarter: 'Q3' },
		{ min: 10, max: 12, quarter: 'Q4' },
	];

	const currentQuarter = quarterRanges.find((range) => month >= range.min && month <= range.max);

	return currentQuarter?.quarter ?? 'Q1';
};

const createQuarterData = (
	quarter: string,
	budget: number = 0,
	balance: number = 0,
	deduction: number = 0
): ICurrentQuarterBudget => ({
	quarter,
	budget_c: budget,
	budget_balance_c: balance,
	budget_deduction_c: deduction,
});

const getCurrentQuarterData = (data: IRbdmBudgetAllocated): ICurrentQuarterBudget => {
	const quarter = getCurrentQuarter();

	const quarterDataMap: Record<string, () => ICurrentQuarterBudget> = {
		Q1: () =>
			createQuarterData(
				'Q1',
				data.quarter_1_budget_c,
				data.quarter_1_budget_balance_c,
				data.quarter_1_budget_deduction_c
			),
		Q2: () =>
			createQuarterData(
				'Q2',
				data.quarter_2_budget_c,
				data.quarter_2_budget_balance_c,
				data.quarter_2_budget_deduction_c
			),
		Q3: () =>
			createQuarterData(
				'Q3',
				data.quarter_3_budget_c,
				data.quarter_3_budget_balance_c,
				data.quarter_3_budget_deduction_c
			),
		Q4: () =>
			createQuarterData(
				'Q4',
				data.quarter_4_budget_c,
				data.quarter_4_budget_balance_c,
				data.quarter_4_budget_deduction_c
			),
	};

	return quarterDataMap[quarter]?.() ?? quarterDataMap.Q1();
};

export const useRbdmBudgetAllocated = (): IQueryResponse<IRbdmBudgetAllocated[]> & {
	currentQuarterData?: ICurrentQuarterBudget;
} => {
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const userRole = profile[0]?.usertype_c;
	const isRBDM = userRole === 'RBDM';

	const fetchRbdmBudgetAllocated = async (): Promise<IRbdmBudgetAllocated[]> => {
		const year_c = getCurrentYear();
		const quarter = getCurrentQuarter();

		return await APIService.getData<IRbdmBudgetAllocated[]>(APICONFIG.RBDM_BUDGET_ALLOCATED, {
			year_c,
			quarter,
			limit: 0,
		});
	};

	const { data, error, isLoading, isPending, isFetching, refetch } = useQuery({
		queryKey: ['rbdmBudgetAllocated', getCurrentYear(), getCurrentQuarter()],
		queryFn: fetchRbdmBudgetAllocated,
		retry: false,
		staleTime: 30 * 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
		enabled: isRBDM,
	});

	const currentQuarterData = data?.length ? getCurrentQuarterData(data[0]) : undefined;

	return {
		data,
		error,
		isLoading,
		isPending,
		isFetching,
		currentQuarterData,
		refetch,
	};
};
